/**
 * RouteCameraController - управляет анимацией камеры при работе с маршрутами.
 *
 * НАЗНАЧЕНИЕ:
 * - Плавно перемещает камеру к маршруту/сегменту при их выборе
 * - Автоматически переключает этаж в FloorSelector
 * - Учитывает ширину sidebar при позиционировании (маршрут центрируется в видимой области)
 *
 * ВЗАИМОДЕЙСТВИЕ С ДРУГИМИ КОМПОНЕНТАМИ:
 * - FloorCameraController: отключается во время анимации (isCameraAnimating=true),
 *   после анимации может плавно корректировать камеру к высоте текущего этажа
 * - FloorSelector: получает currentFloor через setCurrentFloor() для отображения активного этажа
 * - MapControls: использует controlsRef для чтения/записи target камеры
 *
 * ОСОБЕННОСТИ РАСЧЁТА ПОЗИЦИИ КАМЕРЫ:
 * 1. Target камеры привязан к высоте этажа (floorTargetY) для совместимости с FloorCameraController
 * 2. Позиция камеры смещается по Y на deltaY для визуального центрирования многоэтажных маршрутов
 * 3. Камера и target смещаются вправо на половину ширины sidebar, чтобы маршрут был
 *    в центре видимой области (справа от sidebar)
 * 4. Расстояние камеры вычисляется по 3D диагонали bounds, чтобы многоэтажные маршруты
 *    полностью помещались в кадр
 */

import { useFrame, useThree } from "@react-three/fiber";
import { useRef, useEffect, memo } from "react";
import * as THREE from "three";
import { MathUtils } from "three";
import type { PerspectiveCamera, Box3 } from "three";
import type {
  Direction,
  DirectionSegment,
  Point,
} from "../types/directionsTypes";
import {
  computeRouteBounds,
  computeSegmentBounds,
  type RouteBounds,
} from "../utils/routeBounds";
import { FLOOR_HEIGHT } from "../constants/camera";

/**
 * Ширина sidebar в пикселях.
 * Используется для смещения камеры вправо, чтобы маршрут был центрирован
 * в видимой области экрана (не перекрытой sidebar).
 */
const SIDEBAR_WIDTH = 380;

interface RouteCameraControllerProps {
  /** Ref на MapControls для доступа к target камеры */
  controlsRef: React.MutableRefObject<any>;
  /** Текущий маршрут (null если маршрут не построен) */
  direction: Direction | null;
  /** ID выбранного сегмента (null если выбран весь маршрут) */
  selectedSegmentId: number | null;
  /** Флаг для возврата фокуса к полному маршруту (триггер из UI) */
  focusFullRoute: boolean;
  /** Сброс флага focusFullRoute после обработки */
  setFocusFullRoute: (v: boolean) => void;
  /** Установка текущего этажа (обновляет FloorSelector) */
  setCurrentFloor: (floor: number) => void;
  /** Флаг анимации камеры (блокирует FloorCameraController) */
  setIsCameraAnimating: (v: boolean) => void;
  /** Bounding box здания для ограничения камеры */
  bbox: Box3 | null;
}

/**
 * Генерирует уникальный ключ маршрута для отслеживания изменений.
 * Используется для определения, изменился ли маршрут (новые start/end точки).
 */
const getRouteKey = (d: Direction | null): string | null => {
  if (!d) return null;
  const sr = d.segmentationRoute;
  return `${sr.startPointMeshName}|${sr.endPointMeshName}`;
};

/**
 * Вычисляет направление маршрута для ориентации камеры.
 * Камера будет позиционирована так, чтобы смотреть В направлении движения.
 *
 * @param points - Все точки маршрута
 * @param isSegment - true если это сегмент, false если полный маршрут
 * @param segment - Сегмент (только если isSegment=true)
 * @returns Нормализованный вектор направления (горизонтальный)
 */
const computeRouteDirection = (
  points: Point[],
  isSegment: boolean,
  segment?: DirectionSegment,
): THREE.Vector3 => {
  // Конвертация координат в систему Three.js: (x, z, -y)
  const toThreeCoord = (p: Point) => new THREE.Vector3(p.x, p.z, -p.y);

  if (isSegment && segment) {
    // Для сегмента: направление к второй точке или endPoint
    const segmentPointNames = new Set<string>();
    if (segment.startPointMeshName)
      segmentPointNames.add(segment.startPointMeshName);
    segment.pointMeshNames.forEach((n) => segmentPointNames.add(n));
    if (segment.endPointMeshName)
      segmentPointNames.add(segment.endPointMeshName);

    const segmentPoints = points.filter((p) =>
      segmentPointNames.has(p.meshName),
    );

    if (segmentPoints.length >= 2) {
      const start = toThreeCoord(segmentPoints[0]);
      const target = toThreeCoord(segmentPoints[1]);
      const dir = new THREE.Vector3().subVectors(target, start);
      dir.y = 0; // Проекция на горизонталь
      if (dir.length() > 0.001) return dir.normalize();
    }

    // Fallback: от startPoint к endPoint
    if (segment.startPointMeshName && segment.endPointMeshName) {
      const startPoint = points.find(
        (p) => p.meshName === segment.startPointMeshName,
      );
      const endPoint = points.find(
        (p) => p.meshName === segment.endPointMeshName,
      );
      if (startPoint && endPoint) {
        const dir = new THREE.Vector3().subVectors(
          toThreeCoord(endPoint),
          toThreeCoord(startPoint),
        );
        dir.y = 0;
        if (dir.length() > 0.001) return dir.normalize();
      }
    }
  } else {
    // Для полного маршрута: от первой к последней точке
    if (points.length >= 2) {
      const start = toThreeCoord(points[0]);
      const end = toThreeCoord(points[points.length - 1]);
      const dir = new THREE.Vector3().subVectors(end, start);
      dir.y = 0;
      if (dir.length() > 0.001) return dir.normalize();
    }
  }

  // Fallback: дефолтное направление
  return new THREE.Vector3(0.6, 0, 0.6).normalize();
};

const RouteCameraController = memo(function RouteCameraController({
  controlsRef,
  direction,
  selectedSegmentId,
  focusFullRoute,
  setFocusFullRoute,
  setCurrentFloor,
  setIsCameraAnimating,
  bbox,
}: RouteCameraControllerProps) {
  const { camera, gl, invalidate } = useThree();
  const perspectiveCamera = camera as PerspectiveCamera;

  /**
   * Состояние анимации камеры.
   * Используется useFrame для покадровой анимации со сферической интерполяцией.
   */
  const animRef = useRef({
    isAnimating: false,
    startTime: 0,
    duration: 1200, // ms
    // Сферические координаты
    startRadius: 0,
    endRadius: 0,
    startPhi: 0,
    endPhi: 0,
    startTheta: 0,
    endTheta: 0,
    // Target
    startTarget: new THREE.Vector3(),
    endTarget: new THREE.Vector3(),
    // Финальные значения для точной установки
    finalPosition: new THREE.Vector3(),
    finalTarget: new THREE.Vector3(),
  });

  /**
   * Предыдущий маршрут для отслеживания изменений.
   * Позволяет определить, что появился НОВЫЙ маршрут (а не просто перерендер).
   */
  const prevDirectionRef = useRef<Direction | null>(null);

  /**
   * Запускает анимацию камеры к заданным bounds.
   *
   * АЛГОРИТМ:
   * 1. Вычисляет эффективную область экрана (без sidebar)
   * 2. Вычисляет расстояние камеры, чтобы объект занял ~70% эффективной области
   * 3. Позиционирует target на высоте этажа (floorTargetY)
   * 4. Смещает позицию камеры по Y для центрирования многоэтажного маршрута
   * 5. Смещает камеру и target вправо для компенсации sidebar
   * 6. Ориентирует камеру в направлении маршрута
   *
   * @param bounds - Bounding box маршрута/сегмента (center + size)
   * @param targetFloor - Этаж для привязки камеры и FloorSelector
   * @param routeDirection - Направление маршрута для ориентации камеры
   * @param focusPoint - Опциональная точка фокуса (если не указана, используется bounds.center)
   */
  const startAnimation = (
    bounds: RouteBounds,
    targetFloor: number,
    routeDirection: THREE.Vector3,
    focusPoint?: THREE.Vector3,
  ) => {
    const controls = controlsRef.current;
    if (!controls) return;

    // Устанавливаем флаг анимации
    setIsCameraAnimating(true);

    // Автоматически переключаем этаж
    setCurrentFloor(targetFloor);

    // === 1. Размеры viewport и эффективной области ===
    const viewportWidth = gl.domElement.clientWidth;
    const viewportHeight = gl.domElement.clientHeight;
    const effectiveWidth = viewportWidth - SIDEBAR_WIDTH;
    const effectiveAspect = effectiveWidth / viewportHeight;

    // === 2. Размеры объекта для расчёта расстояния камеры ===
    // Угол камеры от вертикали (из cameraOffsetDir: y=0.8, horizontal=1)
    const polarAngle = Math.atan2(1, 0.8); // ≈ 51°

    // Горизонтальный размер: X/Z диагональ + вклад Y в глубину экрана
    // (часть вертикального размера проецируется на горизонталь из-за угла камеры)
    const horizontalSize =
      Math.sqrt(bounds.size.x ** 2 + bounds.size.z ** 2) +
      bounds.size.y * Math.sin(polarAngle);

    // Вертикальный размер: Y + вклад от X/Z
    // (горизонталь мира проецируется на высоту экрана при наклоне камеры)
    const verticalSize =
      bounds.size.y / Math.cos(polarAngle) +
      Math.max(bounds.size.x, bounds.size.z) * Math.sin(polarAngle);

    // === 3. Расстояние камеры ===
    // Объект должен занимать ~70% эффективной области
    const padding = 0.7;
    const fovRad = perspectiveCamera.fov * (Math.PI / 180);

    // Расстояние, чтобы горизонтальная диагональ поместилась по ширине экрана
    const distanceForWidth =
      horizontalSize / (2 * Math.tan(fovRad / 2) * effectiveAspect * padding);
    // Расстояние, чтобы вертикальный размер поместился по высоте экрана
    const distanceForHeight =
      verticalSize / (2 * Math.tan(fovRad / 2) * padding);

    // Берём максимум + минимальный порог
    let distance = Math.max(distanceForWidth, distanceForHeight, 15);

    // Учитываем ограничения controls (minDistance/maxDistance)
    if (controls.minDistance !== undefined) {
      distance = Math.max(distance, controls.minDistance);
    }
    if (controls.maxDistance !== undefined) {
      distance = Math.min(distance, controls.maxDistance);
    }

    // === 4. Вычисляем высоту target на основе этажа (как в FloorCameraController) ===
    const floorTargetY = (targetFloor - 1) * FLOOR_HEIGHT;

    // Точка фокуса: используем focusPoint если передан, иначе центр bounds
    const adjustedCenter = (focusPoint || bounds.center).clone();
    adjustedCenter.y = floorTargetY;

    // === 5. Базовая позиция камеры (без смещения) ===
    // Камера позиционируется позади направления движения, смотрит вперёд
    const horizontalDir = routeDirection.clone().negate(); // Противоположное направление
    const cameraOffsetDir = new THREE.Vector3(
      horizontalDir.x,
      0.8, // Угол обзора сверху
      horizontalDir.z,
    ).normalize();
    const basePosition = adjustedCenter
      .clone()
      .add(cameraOffsetDir.clone().multiplyScalar(distance));

    // Смещаем камеру по Y для центрирования маршрута в поле зрения
    const deltaY = bounds.center.y - floorTargetY;
    basePosition.y += deltaY;

    const baseTarget = adjustedCenter.clone();

    // === 6. Вектор "вправо" камеры ===
    const cameraDirection = new THREE.Vector3()
      .subVectors(baseTarget, basePosition)
      .normalize();
    const worldUp = new THREE.Vector3(0, 1, 0);
    // cross(up, forward) даёт "вправо", cross(forward, up) даёт "влево"
    const rightVector = new THREE.Vector3()
      .crossVectors(worldUp, cameraDirection)
      .normalize();

    // === 7. Смещение в мировых координатах ===
    // Центр эффективной области смещён на SIDEBAR_WIDTH/2 пикселей вправо
    const offsetPixels = SIDEBAR_WIDTH / 2;
    // Видимая ширина на расстоянии distance (для полного viewport)
    const fullAspect = viewportWidth / viewportHeight;
    const visibleWidth = 2 * Math.tan(fovRad / 2) * distance * fullAspect;
    // Пропорция смещения
    const worldOffset = (offsetPixels / viewportWidth) * visibleWidth;

    // === 8. Применяем смещение к позиции и target ===
    const adjustedPosition = basePosition.add(
      rightVector.clone().multiplyScalar(worldOffset),
    );
    const adjustedTarget = baseTarget.add(
      rightVector.clone().multiplyScalar(worldOffset),
    );

    // === 8.1 Clamp target к bounds здания ===
    if (bbox) {
      const boundsPadding = 0.2;
      const minX = bbox.min.x + boundsPadding;
      const maxX = bbox.max.x - boundsPadding;
      const minZ = bbox.min.z + boundsPadding;
      const maxZ = bbox.max.z - boundsPadding;

      // Вычисляем смещение для коррекции target
      const deltaX =
        MathUtils.clamp(adjustedTarget.x, minX, maxX) - adjustedTarget.x;
      const deltaZ =
        MathUtils.clamp(adjustedTarget.z, minZ, maxZ) - adjustedTarget.z;

      // Применяем смещение к target И position (чтобы сохранить направление взгляда)
      adjustedTarget.x += deltaX;
      adjustedTarget.z += deltaZ;
      adjustedPosition.x += deltaX;
      adjustedPosition.z += deltaZ;
    }

    // === 8.2 Проверка polar angle (угол камеры от вертикали) ===
    const maxPolarAngle = Math.PI / 3; // 60° - из MapControls
    const cameraDir = new THREE.Vector3().subVectors(
      adjustedPosition,
      adjustedTarget,
    );
    const currentPolarAngle = Math.acos(cameraDir.y / cameraDir.length());

    if (currentPolarAngle > maxPolarAngle) {
      // Корректируем высоту камеры, чтобы угол не превышал максимум
      const horizontalDist = Math.sqrt(cameraDir.x ** 2 + cameraDir.z ** 2);
      const correctedY = horizontalDist / Math.tan(maxPolarAngle);
      adjustedPosition.y = adjustedTarget.y + correctedY;
    }

    // === 9. Настройка анимации со сферической интерполяцией ===
    // Камера движется по дуге вокруг цели, а не по прямой линии

    const startTarget = controls.target.clone();
    const endTarget = adjustedTarget.clone();

    // Вычисляем сферические координаты камеры относительно target
    const startOffset = camera.position.clone().sub(startTarget);
    const startSpherical = new THREE.Spherical().setFromVector3(startOffset);

    const endOffset = adjustedPosition.clone().sub(endTarget);
    const endSpherical = new THREE.Spherical().setFromVector3(endOffset);

    // Нормализуем theta (азимут) для кратчайшего пути
    let deltaTheta = endSpherical.theta - startSpherical.theta;
    if (deltaTheta > Math.PI) deltaTheta -= 2 * Math.PI;
    if (deltaTheta < -Math.PI) deltaTheta += 2 * Math.PI;
    const normalizedEndTheta = startSpherical.theta + deltaTheta;

    // Адаптивная длительность: учитываем расстояние и угол поворота
    const targetDistance = startTarget.distanceTo(endTarget);
    const distanceDuration = targetDistance * 30; // ms за единицу расстояния
    const angleDuration = (Math.abs(deltaTheta) / Math.PI) * 600; // до 600ms за полный поворот
    const adaptiveDuration = Math.max(300, Math.min(distanceDuration + angleDuration, 1500));

    // Заполняем состояние анимации
    const a = animRef.current;
    a.startTime = performance.now();
    a.duration = adaptiveDuration;
    a.startRadius = startSpherical.radius;
    a.endRadius = endSpherical.radius;
    a.startPhi = startSpherical.phi;
    a.endPhi = endSpherical.phi;
    a.startTheta = startSpherical.theta;
    a.endTheta = normalizedEndTheta;
    a.startTarget.copy(startTarget);
    a.endTarget.copy(endTarget);
    a.finalPosition.copy(adjustedPosition);
    a.finalTarget.copy(endTarget);
    a.isAnimating = true;
  };

  /**
   * ТРИГГЕР 1: Новый маршрут.
   * Срабатывает когда появляется новый маршрут (изменились start/end точки).
   * Камера фокусируется на ВЕСЬ маршрут, этаж = этаж первого сегмента.
   */
  useEffect(() => {
    const currentKey = getRouteKey(direction);
    const prevKey = getRouteKey(prevDirectionRef.current);

    // Проверяем что маршрут именно ИЗМЕНИЛСЯ, а не просто перерендер
    if (direction && currentKey !== prevKey) {
      const bounds = computeRouteBounds(direction.points);
      if (bounds) {
        // Этаж первого сегмента маршрута (откуда пользователь начинает движение)
        const firstSegment = direction.segmentationRoute.segments[0];
        const targetFloor = firstSegment?.floor ?? 1;
        // Направление камеры: от начала к концу маршрута
        const routeDir = computeRouteDirection(direction.points, false);
        startAnimation(bounds, targetFloor, routeDir);
      }
    }

    prevDirectionRef.current = direction;
  }, [direction]);

  /**
   * ТРИГГЕР 2: Выбор сегмента.
   * Срабатывает когда пользователь кликает на шаг маршрута в sidebar.
   * Камера фокусируется на выбранный сегмент, этаж = МИНИМАЛЬНЫЙ этаж сегмента.
   *
   * Почему минимальный этаж:
   * Если сегмент "подняться по лестнице с 2 на 4 этаж", минимальный = 2.
   * Это логичнее, т.к. пользователь видит начало действия.
   */
  useEffect(() => {
    if (selectedSegmentId !== null && direction) {
      const segment = direction.segmentationRoute.segments.find(
        (s) => s.id === selectedSegmentId,
      );
      if (segment) {
        // Собираем все точки сегмента для вычисления bounds
        const names = new Set<string>();
        if (segment.startPointMeshName) names.add(segment.startPointMeshName);
        segment.pointMeshNames.forEach((n) => names.add(n));
        if (segment.endPointMeshName) names.add(segment.endPointMeshName);

        const bounds = computeSegmentBounds(direction.points, names);
        if (bounds) {
          // Находим минимальный этаж среди точек сегмента
          const segmentPoints = direction.points.filter((p) =>
            names.has(p.meshName),
          );
          const floors = segmentPoints.map((p) => p.floor);
          const minFloor =
            floors.length > 0 ? Math.min(...floors) : segment.floor;

          // Направление камеры: ко второй точке сегмента или к endPoint
          const routeDir = computeRouteDirection(
            direction.points,
            true,
            segment,
          );

          // Начальная точка сегмента как точка фокуса
          const startPointData = segment.startPointMeshName
            ? direction.points.find(
                (p) => p.meshName === segment.startPointMeshName,
              )
            : segmentPoints[0];

          const focusPoint = startPointData
            ? new THREE.Vector3(
                startPointData.x,
                startPointData.z,
                -startPointData.y,
              )
            : undefined;

          startAnimation(bounds, minFloor, routeDir, focusPoint);
        }
      }
    }
  }, [selectedSegmentId, direction]);

  /**
   * ТРИГГЕР 3: Кнопка "вернуться к полному маршруту".
   * Срабатывает когда пользователь хочет увидеть весь маршрут после просмотра сегмента.
   * Камера фокусируется на ВЕСЬ маршрут, этаж = этаж первого сегмента.
   */
  useEffect(() => {
    if (focusFullRoute && direction) {
      const bounds = computeRouteBounds(direction.points);
      if (bounds) {
        // Возвращаемся на этаж первого сегмента
        const firstSegment = direction.segmentationRoute.segments[0];
        const targetFloor = firstSegment?.floor ?? 1;
        // Направление камеры: от начала к концу маршрута
        const routeDir = computeRouteDirection(direction.points, false);
        startAnimation(bounds, targetFloor, routeDir);
      }
      setFocusFullRoute(false);
    }
  }, [focusFullRoute, direction, setFocusFullRoute]);

  /**
   * Покадровая анимация камеры со сферической интерполяцией.
   * Easing: smoothstep (плавный старт и конец)
   */
  useFrame(() => {
    const controls = controlsRef.current;
    const a = animRef.current;
    if (!controls || !a.isAnimating) return;

    const elapsed = performance.now() - a.startTime;
    const rawProgress = Math.min(elapsed / a.duration, 1);

    // Smoothstep easing: плавный старт и конец (аналог power2.inOut)
    const t = rawProgress * rawProgress * (3 - 2 * rawProgress);

    // Интерполируем target линейно
    controls.target.lerpVectors(a.startTarget, a.endTarget, t);

    // Интерполируем сферические координаты
    const radius = MathUtils.lerp(a.startRadius, a.endRadius, t);
    const phi = MathUtils.lerp(a.startPhi, a.endPhi, t);
    const theta = MathUtils.lerp(a.startTheta, a.endTheta, t);

    // Конвертируем в декартовы координаты
    const spherical = new THREE.Spherical(radius, phi, theta);
    const offset = new THREE.Vector3().setFromSpherical(spherical);

    // Позиция камеры = target + offset
    camera.position.copy(controls.target).add(offset);

    controls.update();
    invalidate();

    // Завершение анимации
    if (rawProgress >= 1) {
      camera.position.copy(a.finalPosition);
      controls.target.copy(a.finalTarget);
      controls.update();
      a.isAnimating = false;
      setIsCameraAnimating(false);
      invalidate();
    }
  });

  // Компонент не рендерит никакой JSX (чисто логика)
  return null;
});

export default RouteCameraController;
